import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ChevronRight, BookOpen, Video, Award, Users, Star, CheckCircle, Globe, TrendingUp, Zap, Play } from 'lucide-react';
import LiveNotifications from './LiveNotifications';
import SEO from './SEO';
import Footer from './Footer';

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Homepage Schema - Organization, WebSite, and Course
  const homepageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://enrolment.bookreviewspecialists.app/#organization",
        "name": "Video Book Review Specialist",
        "url": "https://enrolment.bookreviewspecialists.app",
        "logo": {
          "@type": "ImageObject",
          "url": "https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
        },
        "sameAs": [
          "https://www.facebook.com/groups/449766771396718/",
          "https://www.youtube.com/@dianeshaweauthor"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "info@virtualautochat.uk",
          "contactType": "customer service"
        }
      },
      {
        "@type": "WebSite",
        "@id": "https://enrolment.bookreviewspecialists.app/#website",
        "url": "https://enrolment.bookreviewspecialists.app",
        "name": "Video Book Review Specialist",
        "description": "CPD Accredited Training to become a Video Book Review Specialist",
        "publisher": {
          "@id": "https://enrolment.bookreviewspecialists.app/#organization"
        }
      },
      {
        "@type": "Course",
        "@id": "https://enrolment.bookreviewspecialists.app/#course",
        "name": "Certified Video Book Review Specialist Course",
        "description": "Learn to create AI-powered, faceless book review videos. No reading, no camera, no scripts required. CPD Accredited certification program.",
        "provider": {
          "@id": "https://enrolment.bookreviewspecialists.app/#organization"
        },
        "educationalCredentialAwarded": "CPD Accredited Video Book Review Specialist Certificate",
        "courseMode": "Online",
        "isAccessibleForFree": false,
        "offers": [
          {
            "@type": "Offer",
            "price": "9.99",
            "priceCurrency": "GBP",
            "name": "Enrolment Fee",
            "availability": "https://schema.org/InStock"
          },
          {
            "@type": "Offer",
            "price": "75.00",
            "priceCurrency": "GBP",
            "name": "Foundation Course",
            "availability": "https://schema.org/InStock"
          },
          {
            "@type": "Offer",
            "price": "215.00",
            "priceCurrency": "GBP",
            "name": "Pro Course",
            "availability": "https://schema.org/InStock"
          }
        ]
      }
    ]
  };

  const testimonials = [
    {
      text: "This course transformed my understanding of the book review industry. I went from zero to earning my first £500 within 3 weeks!",
      author: "Sarah Johnson",
      role: "Content Creator"
    },
    {
      text: "The AI tools and strategies taught here are game-changing. I'm now running my own book review agency with 12 clients.",
      author: "Michael Chen",
      role: "Agency Owner"
    },
    {
      text: "Professional, comprehensive, and incredibly practical. The certification opened doors I never knew existed.",
      author: "Emma Williams",
      role: "Freelance Reviewer"
    }
  ];

  const foundationModules = [
    {
      title: "Welcome & Getting Set Up",
      description: "Complete orientation and platform setup for immediate productivity"
    },
    {
      title: "Understanding the Book Market",
      description: "Deep dive into market analysis, niches, and profitable opportunities"
    },
    {
      title: "AI Review Creation (No Reading)",
      description: "Master AI tools to create authentic, engaging reviews efficiently"
    },
    {
      title: "Video Creation Using AI Tools",
      description: "Professional video content creation using cutting-edge AI technology"
    },
    {
      title: "Getting Paid for Reviews",
      description: "Monetization strategies, pricing, and client acquisition methods"
    },
    {
      title: "Certification & Growth Plan",
      description: "Official certification process and strategic growth planning"
    }
  ];

  const proModules = [
    {
      title: "Brand Building",
      description: "Develop a powerful personal brand that attracts high-paying clients"
    },
    {
      title: "Funnels & Offers",
      description: "Create automated sales funnels and irresistible service packages"
    },
    {
      title: "Monetization with YouTube & TikTok",
      description: "Scale your reach and revenue through social media platforms"
    },
    {
      title: "Automated Client Generation",
      description: "Build systems that bring clients to you on autopilot"
    },
    {
      title: "Asset Delivery",
      description: "A landing page that sells your services with testimonials and registration page with back office admin login. (terms and conditions apply)"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-25 to-indigo-50">
      <SEO 
        title="Video Book Review Specialist | CPD Accredited Training Course"
        description="Become a certified Video Book Review Specialist with our CPD Accredited training course. Learn to create AI-powered book review videos without reading, being on camera, or writing scripts. Join 500+ certified specialists. Enrol from £9.99."
        canonical="/"
        schema={homepageSchema}
      />
      
      {/* Full Width GIF Header */}
      <div className="w-full">
        <img 
          src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/csgwpycc_train%20to%20become%20a%20video%20review%20specialist.gif"
          alt="Train to Become a Video Review Specialist"
          className="w-full h-48 md:h-64 lg:h-80 object-cover object-center"
        />
        <div className="bg-white py-6">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              Enrolment Registration
            </h1>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="navbar">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <a href="https://bookreviewspecialists.app/" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <img 
                src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
                alt="Book Review Specialists Logo"
                className="w-10 h-10 rounded-full object-cover"
              />
              <span className="text-xl font-bold text-slate-800">Book Review Specialists</span>
            </a>
            <div className="hidden md:flex items-center space-x-6">
              <a href="#overview" className="nav-link">Overview</a>
              <a href="#pricing" className="nav-link">Pricing</a>
              <a href="#testimonials" className="nav-link">Reviews</a>
              <Link to="/become-a-video-book-review-specialist" className="nav-link text-purple-600 hover:text-purple-700 font-medium">
                Become a Specialist
              </Link>
              <Link to="/book-video-reviews-for-authors" className="nav-link text-pink-600 hover:text-pink-700 font-medium">
                For Authors
              </Link>
              <Link to="/blog-insights" className="nav-link text-blue-600 hover:text-blue-700 font-medium">
                Blog
              </Link>
              <Link to="/student-login" className="nav-link text-green-600 hover:text-green-700 font-medium">
                Student Dashboard
              </Link>
              <Link to="/enroll" className="btn btn-primary">Enroll Now</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Waiting List Banner */}
      <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 py-3 px-4 sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto">
          <a 
            href="https://qualiquiz.app/quiz/join-the-official-waiting-list-train-as-a-cpd-video-book-review-specialist"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center text-white hover:opacity-90 transition-opacity"
          >
            <span className="text-lg font-bold mr-3">🎯 Join Official Waiting List</span>
            <ChevronRight className="w-5 h-5 animate-pulse" />
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero">
        <div className="container mx-auto px-4 relative z-10">
          <div className={`text-center ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <Badge className="mb-6 bg-blue-100 text-blue-800 border-blue-200">
              🎓 Professional Certification Program
            </Badge>
            <h1 className="hero-title">
              Become a Certified<br />
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Video Book Review Specialist
              </span>
            </h1>
            
            {/* YouTube Shorts Video Embed */}
            <div className="mt-8 mb-8 flex justify-center">
              <div className="relative w-full max-w-md">
                <div className="aspect-[9/16] rounded-xl overflow-hidden shadow-2xl border-4 border-white">
                  <iframe
                    src="https://www.youtube.com/embed/IBEZPCMdd28?autoplay=0&mute=1&loop=1&playlist=IBEZPCMdd28"
                    title="Video Book Review Specialist Training"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
            
            <p className="hero-subtitle">
              Master the art of AI-powered book reviews and build a thriving career in the digital content industry. 
              No reading required - just smart strategies and cutting-edge tools.
            </p>
            
            {/* Stats and Trust Badges */}
            <div className="flex flex-wrap justify-center items-center gap-6 mt-8 mb-8">
              <Badge className="bg-green-100 text-green-800 border-green-200 px-4 py-2 text-sm font-semibold">
                <Users className="w-4 h-4 mr-2" />
                500+ Certified Specialists
              </Badge>
              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 px-4 py-2 text-sm font-semibold">
                <Star className="w-4 h-4 mr-2" />
                4.9/5 Student Rating  
              </Badge>
              <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-4 py-2 text-sm font-semibold">
                <Award className="w-4 h-4 mr-2" />
                Industry Recognized
              </Badge>
            </div>

            {/* CPD Accredited Badge */}
            <div className="flex justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_edu-signups/artifacts/whbb2v24_Cpd%20ACCREDITED_video%20book%20review%20course.gif"
                alt="CPD Accredited Course"
                className="w-24 h-24 object-contain"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
              <Link to="/enroll" className="btn btn-primary btn-large">
                Start Your Journey <ChevronRight className="w-5 h-5 ml-2" />
              </Link>
              <a 
                href="https://bookreviewspecialists.app/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-secondary btn-large"
              >
                Learn More
              </a>
            </div>
            {/* Student Dashboard removed from hero - available in main nav and footer */}
            <div className="flex items-center justify-center mt-6 space-x-8 text-sm text-slate-600">
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-2" />
                500+ Certified Specialists
              </div>
              <div className="flex items-center">
                <Star className="w-4 h-4 mr-2 text-yellow-500" />
                4.9/5 Student Rating
              </div>
              <div className="flex items-center">
                <Award className="w-4 h-4 mr-2" />
                Industry Recognized
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dual Banner Section */}
      <section className="py-8 bg-slate-100">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/become-a-video-book-review-specialist" className="block hover:opacity-95 transition-opacity hover:scale-[1.02] transform duration-300">
              <img 
                src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/l05izu7i_IMG_20260331_192507.jpg"
                alt="Train to become a Certified Video Book Reviewer - Click to learn more"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </Link>
            <Link to="/book-video-reviews-for-authors" className="block hover:opacity-95 transition-opacity hover:scale-[1.02] transform duration-300">
              <img 
                src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/b2ljrvff_IMG_20260331_192435.jpg"
                alt="Do you need a Video Book Review, Trailer or Promo Video - Click for more info"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section id="overview" className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Why Choose Our CPD Accredited Training Course?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The only comprehensive CPD Accredited certification program that combines AI technology, 
              proven strategies, and industry expertise to fast-track your success.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="card text-center">
              <Zap className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">AI-Powered Efficiency</h3>
              <p className="text-slate-600">
                Learn to create professional book reviews using advanced AI tools - no reading required.
              </p>
            </Card>
            <Card className="card text-center">
              <TrendingUp className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Proven Income Strategies</h3>
              <p className="text-slate-600">
                Access tested monetization methods that our graduates use to earn £75-£500+ per review.
              </p>
            </Card>
            <Card className="card text-center">
              <Globe className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Global Opportunities</h3>
              <p className="text-slate-600">
                Work with publishers, authors, and agencies worldwide from the comfort of your home.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
              How It Works
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Simple steps to start your journey as a Video Book Review Specialist
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="p-8 text-center border-0 shadow-xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                1
              </div>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 mt-4">
                <BookOpen className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Enrol</h3>
              <p className="text-slate-600">
                Select course date, choose your course level, and login to get started.
              </p>
            </Card>

            <Card className="p-8 text-center border-0 shadow-xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold">
                2
              </div>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 mt-4">
                <Award className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Learn & Get Qualified</h3>
              <p className="text-slate-600">
                Complete your training and receive your CPD Accredited Certificate.
              </p>
            </Card>

            <Card className="p-8 text-center border-0 shadow-xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                3
              </div>
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 mt-4">
                <Video className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Start Reviewing</h3>
              <p className="text-slate-600">
                Access the Video Book Review Platform and begin creating reviews.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Choose Your Path</h2>
            <p className="text-lg text-slate-600">
              Start with our enrollment fee, then select the course level that matches your goals.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Enrollment */}
            <Card className="pricing-card">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2">Enrollment Fee</h3>
                <div className="price mb-4">
                  <span className="price-currency">£</span>9.99
                </div>
                <p className="text-slate-600 mb-6">
                  Secure your spot and access to course selection
                </p>
                <ul className="feature-list text-left mb-8">
                  <li>Course introduction materials</li>
                  <li>Industry report download</li>
                  <li>Side hustles guide</li>
                  <li>Business development resources</li>
                  <li><strong>BONUS: Ultimate Guide to Selling More Ebooks Report</strong></li>
                </ul>
                <Link to="/enroll" className="btn btn-secondary w-full">
                  Get Started
                </Link>
              </div>
            </Card>

            {/* Foundation */}
            <Card className="pricing-card">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2">Foundation Course</h3>
                <div className="price mb-4">
                  <span className="price-currency">£</span>75.00
                </div>
                <p className="text-slate-600 mb-6">
                  Essential skills for book review professionals
                </p>
                <ul className="feature-list text-left mb-8">
                  <li>6 comprehensive modules</li>
                  <li>AI review creation mastery</li>
                  <li>Video production training</li>
                  <li>Monetization strategies</li>
                  <li>Professional certification</li>
                  <li>Growth planning session</li>
                </ul>
                <Link to="/enroll" className="btn btn-primary w-full">
                  Choose Foundation
                </Link>
              </div>
            </Card>

            {/* Pro Course */}
            <Card className="pricing-card featured">
              <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white">
                Most Popular
              </Badge>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2">Pro Course</h3>
                <div className="price mb-4">
                  <span className="price-currency">£</span>215.00
                </div>
                <p className="text-slate-600 mb-6">
                  Complete mastery + advanced business strategies
                </p>
                <ul className="feature-list text-left mb-8">
                  <li>Everything in Foundation</li>
                  <li>5 advanced modules</li>
                  <li>Brand building masterclass</li>
                  <li>Client Chatbot Training funnel creation</li>
                  <li>Landing Page and ecommerce website</li>
                  <li>Automated client systems</li>
                  <li>Agency scaling strategies</li>
                  <li>6 video testimonials</li>
                  <li>Licensing opportunities</li>
                </ul>
                <Link to="/enroll" className="btn btn-primary w-full">
                  Choose Pro
                </Link>
              </div>
            </Card>
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-slate-600">
              💡 <strong>Smart Tip:</strong> Pay enrollment fee first, then return to select your course level
            </p>
          </div>
        </div>
      </section>

      {/* Course Modules */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">What You'll Master</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Foundation Modules */}
            <div>
              <h3 className="text-2xl font-semibold mb-6 text-blue-600">Foundation Course (6 Modules)</h3>
              <div className="modules-grid">
                {foundationModules.map((module, index) => (
                  <Card key={index} className="module-card">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="module-title">{module.title}</h4>
                        <p className="module-description">{module.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Pro Modules */}
            <div>
              <h3 className="text-2xl font-semibold mb-6 text-purple-600">Pro Course (Additional 5 Modules)</h3>
              <div className="modules-grid">
                {proModules.map((module, index) => (
                  <Card key={index} className="module-card">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-purple-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="module-title">{module.title}</h4>
                        <p className="module-description">{module.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
          
          {/* Secure Payment Banner */}
          <div className="mt-12">
            <Link to="/enroll" className="block hover:opacity-95 transition-opacity">
              <img 
                src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/4z0hnbis_How%20bookreview%20takes%20payment.jpg"
                alt="We Accept Secure Payments - Visa, Mastercard, Revolut, Stripe, Clearpay - 100% Secure Checkout"
                className="w-full max-w-4xl mx-auto h-auto rounded-lg shadow-lg"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="section bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Success Stories</h2>
            <p className="text-lg text-slate-600">
              Join hundreds of certified specialists building thriving careers
            </p>
          </div>

          {/* Video Testimonials Grid */}
          <div className="mb-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {/* Video 1 */}
              <Card className="bg-white shadow-xl border-0 overflow-hidden">
                <div className="aspect-video bg-black">
                  <iframe
                    src="https://www.youtube.com/embed/QiWf9SJs88Y"
                    title="Course Changed My Life"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-4 text-center">
                  <div className="flex justify-center mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm mb-1">
                    Course Changed My Life
                  </h3>
                  <p className="text-slate-600 text-xs">
                    "Find out how this £9.99 Course transformed my business"
                  </p>
                </div>
              </Card>

              {/* Video 2 */}
              <Card className="bg-white shadow-xl border-0 overflow-hidden">
                <div className="aspect-video bg-black">
                  <iframe
                    src="https://www.youtube.com/embed/YtTUMtMirmA"
                    title="Amazing Results"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-4 text-center">
                  <div className="flex justify-center mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm mb-1">
                    Amazing Results
                  </h3>
                  <p className="text-slate-600 text-xs">
                    Real student sharing their video book review success
                  </p>
                </div>
              </Card>

              {/* Video 3 */}
              <Card className="bg-white shadow-xl border-0 overflow-hidden">
                <div className="aspect-video bg-black">
                  <iframe
                    src="https://www.youtube.com/embed/Qoj99Pc75BY"
                    title="Professional Growth"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-4 text-center">
                  <div className="flex justify-center mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm mb-1">
                    Professional Growth
                  </h3>
                  <p className="text-slate-600 text-xs">
                    How the course transformed their content creation skills
                  </p>
                </div>
              </Card>

              {/* Video 4 */}
              <Card className="bg-white shadow-xl border-0 overflow-hidden">
                <div className="aspect-video bg-black">
                  <iframe
                    src="https://www.youtube.com/embed/n37FbnwISxo"
                    title="Incredible Journey"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-4 text-center">
                  <div className="flex justify-center mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm mb-1">
                    Incredible Journey
                  </h3>
                  <p className="text-slate-600 text-xs">
                    Student testimonial about their specialist journey
                  </p>
                </div>
              </Card>

              {/* Video 5 */}
              <Card className="bg-white shadow-xl border-0 overflow-hidden">
                <div className="aspect-video bg-black">
                  <iframe
                    src="https://www.youtube.com/embed/eJDhV0a0bkQ"
                    title="Life-Changing Results"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-4 text-center">
                  <div className="flex justify-center mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm mb-1">
                    Life-Changing Results
                  </h3>
                  <p className="text-slate-600 text-xs">
                    Real transformation through video book review training
                  </p>
                </div>
              </Card>

              {/* Video 6 */}
              <Card className="bg-white shadow-xl border-0 overflow-hidden">
                <div className="aspect-video bg-black">
                  <iframe
                    src="https://www.youtube.com/embed/_T8NvjnOj48"
                    title="Success Story"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-4 text-center">
                  <div className="flex justify-center mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm mb-1">
                    Success Story
                  </h3>
                  <p className="text-slate-600 text-xs">
                    Another amazing student sharing their course experience
                  </p>
                </div>
              </Card>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div>
                  <p className="testimonial-author">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Career?</h2>
          <p className="text-xl text-blue-100 mb-6 max-w-2xl mx-auto">
            Join the next cohort of certified video book review specialists and start earning within weeks.
          </p>
          
          {/* CPD Accredited Badge */}
          <div className="mb-6 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_edu-signups/artifacts/whbb2v24_Cpd%20ACCREDITED_video%20book%20review%20course.gif"
              alt="CPD Accredited Course"
              className="rounded-lg shadow-lg"
              style={{width: '150px', height: '150px'}}
            />
          </div>
          
          <Link to="/enroll" className="btn bg-white text-blue-600 hover:bg-slate-50 btn-large">
            Enroll Now - Starting at £9.99
          </Link>
          <div className="mt-6 text-blue-200">
            <p>✓ Instant digital access  ✓ Lifetime access  ✓ Industry certification</p>
          </div>
        </div>
      </section>

      {/* Live Notifications */}
      <LiveNotifications />

      {/* More Info Banner - Before Footer */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 py-8 px-4">
        <div className="container mx-auto text-center">
          <a 
            href="https://bookreviewspecialists.app/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block group"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-6 border-2 border-white/30 hover:bg-white/20 transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-center space-x-4">
                <Globe className="w-8 h-8 text-white" />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">More Info on Main Website</h3>
                  <p className="text-white/80 text-sm">Visit bookreviewspecialists.app for full details</p>
                </div>
                <ChevronRight className="w-6 h-6 text-white group-hover:translate-x-2 transition-transform" />
              </div>
            </div>
          </a>
        </div>
      </div>

      {/* About the Developer Banner */}
      <Link to="/about-developer" className="block w-full hover:opacity-95 transition-opacity">
        <img 
          src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/pcvl1d1w_ThumbnailMaker_31032026_145816.png"
          alt="About the Developer - Diane Shawe"
          className="w-full h-auto object-cover"
        />
      </Link>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;